import {
  Button,
  Col,
  Divider,
  Flex,
  Form,
  Row,
  Select,
  Modal,
  Alert,
} from "antd";
import { notification } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";

import "./ProjectListForm.scss";
import { IProject } from "projects/models";
import { ProjectStages } from "projects/constants";
import { ProjectAccountingInformation } from "../project-accounting-information/ProjectAccountingInformation";
import { ProjectContactInformation } from "../project-contact-information/ProjectContactInformation";
import { ProjectInformation } from "../project-information/ProjectInformation";
import { ProjectInstallationInformation } from "../project-installation-information/ProjectInstallationInformation";
import { ProjectSalesInformation } from "../project-sales-information/ProjectSalesInformation";
import { useUpdateProject } from "projects/hooks";
import dayjs from "dayjs";
import { isEmpty, omit, sortBy } from "lodash";
import { memo, useCallback, useEffect, useRef } from "react";
import { AlertRef } from "antd/es/alert/Alert";
import MentionsComponent from "shared/components/mentions/Mentions";
import { useUserMentionOptions } from "users/queries";
import { useLookupOptions } from "admin/queries";
import { useBoundStore } from "store";
import { UserRoles } from "admin/constants";

type Props = {
  project: IProject;
  onFinish?: () => Promise<void>;
  onCancel?: () => void;
  isEditing?: boolean;
};

export const convertDayjsToDate = (obj: any) => {
  for (const key in obj) {
    if (obj[key] !== null && dayjs.isDayjs(obj[key])) {
      obj[key] = obj[key].toDate();
    }
  }
  return obj;
};

export const ProjectListForm = memo(
  ({ project, onFinish, onCancel, isEditing = true }: Props) => {
    const [form] = Form.useForm();
    const { error, data, updateProject, success, warnings } = useUpdateProject();
    const [modal, contextHolder] = Modal.useModal();
    const errorRef = useRef<null | AlertRef>(null);
    // Track the last successfully updated project ID to show notification only once per update
    const lastSuccessDataRef = useRef<IProject | undefined>(undefined);
    const { user, currentOrganization } = useBoundStore();
    const { data: userMentionOptions = [] } = useUserMentionOptions(currentOrganization?.id);
    const { data: projectStatusOptions = [] } = useLookupOptions("project_status");

    const executeScroll = useCallback(
      () => errorRef?.current?.nativeElement?.scrollIntoView(true),
      [],
    );

    const scrollToTop = useCallback(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const updateProjectWithDefaultValues = useCallback(
      (p: IProject) => ({
        ...p,
        address: p?.address ?? "",
        city: p?.city ?? "",
        state: p?.state ?? "",
        zip: p?.zip ?? "",
        ownerEmail: p?.ownerEmail ?? "",
        ownerPhone: p?.ownerPhone ?? "",
        lightingSubcontractorId: p?.lightingSubcontractorId ?? "",
        hvacSubcontractorId: p?.hvacSubcontractorId ?? "",
        refrigerationSubcontractorId: p?.refrigerationSubcontractorId ?? "",
        salesConfigurationId: p?.salesConfigurationId ?? "",
        latestProjectNote: undefined,
        stage: p?.stage ?? "",
        status: p?.status ?? "",
        ownerName: p?.ownerName ?? "",
        utilityProgram: p?.utilityProgram ?? "",
        projectTypes: p?.projectTypes ?? [],
        customerPaymentMethod: p?.customerPaymentMethod ?? "",
        percentToClose: p?.percentToClose ?? "",
        ownerOrTenantPayee: p?.ownerOrTenantPayee ?? "",
        utilityBillsReceivedAndReviewed: !isEmpty(
          p?.utilityBillsReceivedAndReviewed,
        )
          ? dayjs(p?.utilityBillsReceivedAndReviewed)
          : null,
        auditRequestFormReceivedReviewed: !isEmpty(
          p?.auditRequestFormReceivedReviewed,
        )
          ? dayjs(p?.auditRequestFormReceivedReviewed)
          : null,
        auditScheduleDate: !isEmpty(p?.auditScheduleDate)
          ? dayjs(p?.auditScheduleDate)
          : null,
        onsiteAuditDone: !isEmpty(p?.onsiteAuditDone)
          ? dayjs(p?.onsiteAuditDone)
          : null,
        ceToolDone: !isEmpty(p?.ceToolDone) ? dayjs(p?.ceToolDone) : null,
        hvacAssessmentFormDone: !isEmpty(p?.hvacAssessmentFormDone)
          ? dayjs(p?.hvacAssessmentFormDone)
          : null,
        hvacPrePhotosReceived: !isEmpty(p?.hvacPrePhotosReceived)
          ? dayjs(p?.hvacPrePhotosReceived)
          : null,
        sentToHcnForQuote: !isEmpty(p?.sentToHcnForQuote)
          ? dayjs(p?.sentToHcnForQuote)
          : null,
        hcnQuotePackageReceived: !isEmpty(p?.hcnQuotePackageReceived)
          ? dayjs(p?.hcnQuotePackageReceived)
          : null,
        hvacQuoteSubsCertsApproved: !isEmpty(p?.hvacQuoteSubsCertsApproved)
          ? dayjs(p?.hvacQuoteSubsCertsApproved)
          : null,
        proposalSentToSalesRep: !isEmpty(p?.proposalSentToSalesRep)
          ? dayjs(p?.proposalSentToSalesRep)
          : null,
        estimatedSaleDate: !isEmpty(p?.estimatedSaleDate)
          ? dayjs(p?.estimatedSaleDate)
          : null,
        soldYear: p?.soldYear,
        soldMonth: p?.soldMonth,
        soldQuarter: p?.soldQuarter,
        reviewedSignedContractDocuments: !isEmpty(
          p?.reviewedSignedContractDocuments,
        )
          ? dayjs(p?.reviewedSignedContractDocuments)
          : null,
        submittedToUtilityForApproval: !isEmpty(
          p?.submittedToUtilityForApproval,
        )
          ? dayjs(p?.submittedToUtilityForApproval)
          : null,
        submittedForFinancingApproval: !isEmpty(
          p?.submittedForFinancingApproval,
        )
          ? dayjs(p?.submittedForFinancingApproval)
          : null,
        financingApproved: !isEmpty(p?.financingApproved)
          ? dayjs(p?.financingApproved)
          : null,
        utilityPreInspectionDate: !isEmpty(p?.utilityPreInspectionDate)
          ? dayjs(p?.utilityPreInspectionDate)
          : null,
        utilityPreInspectionDone: !isEmpty(p?.utilityPreInspectionDone)
          ? dayjs(p?.utilityPreInspectionDone)
          : null,
        utilityApprovalLetter: !isEmpty(p?.utilityApprovalLetter)
          ? dayjs(p?.utilityApprovalLetter)
          : null,
        customerWelcomeLetterSent: !isEmpty(p?.customerWelcomeLetterSent)
          ? dayjs(p?.customerWelcomeLetterSent)
          : null,
        mechanicalPOSent: !isEmpty(p?.mechanicalPOSent)
          ? dayjs(p?.mechanicalPOSent)
          : null,
        lightingLaborPOSent: !isEmpty(p?.lightingLaborPOSent)
          ? dayjs(p?.lightingLaborPOSent)
          : null,
        lightingMaterialPOSent: !isEmpty(p?.lightingMaterialPOSent)
          ? dayjs(p?.lightingMaterialPOSent)
          : null,
        refrigerationPOSent: !isEmpty(p?.refrigerationPOSent)
          ? dayjs(p?.refrigerationPOSent)
          : null,
        refrigerationETA: !isEmpty(p?.refrigerationETA)
          ? dayjs(p?.refrigerationETA)
          : null,
        hvacETA: !isEmpty(p?.hvacETA) ? dayjs(p?.hvacETA) : null,
        lightingETA: !isEmpty(p?.lightingETA) ? dayjs(p?.lightingETA) : null,
        refrigerationInstalled: !isEmpty(p?.refrigerationInstalled)
          ? dayjs(p?.refrigerationInstalled)
          : null,
        hvacInstalled: !isEmpty(p?.hvacInstalled)
          ? dayjs(p?.hvacInstalled)
          : null,
        lightingInstalled: !isEmpty(p?.lightingInstalled)
          ? dayjs(p?.lightingInstalled)
          : null,
        lightingMaterialPOAmount: p?.lightingMaterialPOAmount,
        lightingLaborPOAmount: p?.lightingLaborPOAmount,
        mechanicalPOAmount: p?.mechanicalPOAmount,
        refrigerationPOAmount: p?.refrigerationPOAmount,
        totalProjectInstalledDate: !isEmpty(p?.totalProjectInstalledDate)
          ? dayjs(p?.totalProjectInstalledDate)
          : null,
        totalProjectCompletedDate: !isEmpty(p?.totalProjectCompletedDate)
          ? dayjs(p?.totalProjectCompletedDate)
          : null,
        closeoutPackageReceived: !isEmpty(p?.closeoutPackageReceived)
          ? dayjs(p?.closeoutPackageReceived)
          : null,
        closeoutPackageReviewed: !isEmpty(p?.closeoutPackageReviewed)
          ? dayjs(p?.closeoutPackageReviewed)
          : null,
        completionDocumentSentToCustomer: !isEmpty(
          p?.completionDocumentSentToCustomer,
        )
          ? dayjs(p?.completionDocumentSentToCustomer)
          : null,
        closeoutPackageSubmittedToUtility: !isEmpty(
          p?.closeoutPackageSubmittedToUtility,
        )
          ? dayjs(p?.closeoutPackageSubmittedToUtility)
          : null,
        utilityPostInspectionScheduleDate: !isEmpty(
          p?.utilityPostInspectionScheduleDate,
        )
          ? dayjs(p?.utilityPostInspectionScheduleDate)
          : null,
        utilityPostInspectionCompleteDate: !isEmpty(
          p?.utilityPostInspectionCompleteDate,
        )
          ? dayjs(p?.utilityPostInspectionCompleteDate)
          : null,
        utilityEcd: !isEmpty(p?.utilityEcd)
          ? dayjs(p?.utilityEcd)
          : null,
        accountManagerCommissionAmount: p?.accountManagerCommissionAmount,
        leadGeneratorCommissionAmount: p?.leadGeneratorCommissionAmount,
        salesSupportCommissionAmount: p?.salesSupportCommissionAmount,
        commissionPaid: !isEmpty(p?.commissionPaid)
          ? dayjs(p?.commissionPaid)
          : null,
        soldTotalProjectValue: p?.soldTotalProjectValue,
        soldTotalIncentive: p?.soldTotalIncentive,
        soldTotalCustomerPortion: p?.soldTotalCustomerPortion,
        soldLtgValue: p?.soldLtgValue,
        soldHvacValue: p?.soldHvacValue,
        soldRefrigerationValue: p?.soldRefrigerationValue,
        soldBudgetCosts: p?.soldBudgetCosts,
        soldProfit: p?.soldProfit,
        soldMargin: Number((Number(p?.soldMargin ?? 0) * 100)?.toFixed(2)),
        billedDepositDate: !isEmpty(p?.billedDepositDate)
          ? dayjs(p?.billedDepositDate)
          : null,
        billedDepositAmount: p?.billedDepositAmount,
        depositReceivedDate: !isEmpty(p?.depositReceivedDate)
          ? dayjs(p?.depositReceivedDate)
          : null,
        depositReceivedAmount: p?.depositReceivedAmount,
        depositClearedDate: !isEmpty(p?.depositClearedDate)
          ? dayjs(p?.depositClearedDate)
          : null,
        billedIncentiveDate: !isEmpty(p?.billedIncentiveDate)
          ? dayjs(p?.billedIncentiveDate)
          : null,
        billedIncentiveAmount: p?.billedIncentiveAmount,
        billedIncentiveReceivedDate: !isEmpty(p?.billedIncentiveReceivedDate)
          ? dayjs(p?.billedIncentiveReceivedDate)
          : null,
        billedIncentiveReceivedAmount: p?.billedIncentiveReceivedAmount,
        billedFinancedPortionDate: !isEmpty(p?.billedFinancedPortionDate)
          ? dayjs(p?.billedFinancedPortionDate)
          : null,
        billedFinancedPortionAmount: p?.billedFinancedPortionAmount,
        financedPortionReceivedDate: !isEmpty(p?.financedPortionReceivedDate)
          ? dayjs(p?.financedPortionReceivedDate)
          : null,
        financedPortionReceivedAmount: p?.financedPortionReceivedAmount,
        overageCustomerPortionDate: !isEmpty(p?.overageCustomerPortionDate)
          ? dayjs(p?.overageCustomerPortionDate)
          : null,
        overageCustomerPortionAmount: p?.overageCustomerPortionAmount,
        overageCustomerPortionReceivedDate: !isEmpty(
          p?.overageCustomerPortionReceivedDate,
        )
          ? dayjs(p?.overageCustomerPortionReceivedDate)
          : null,
        overageCustomerPortionReceivedAmount:
          p?.overageCustomerPortionReceivedAmount,
        accrualDate: !isEmpty(p?.accrualDate) ? dayjs(p?.accrualDate) : null,
        financeLender: p?.financeLender ?? "",
        financeExpirationDate: !isEmpty(p?.financeExpirationDate)
          ? dayjs(p?.financeExpirationDate)
          : null,
        utilityExpirationDate: !isEmpty(p?.utilityExpirationDate)
          ? dayjs(p?.utilityExpirationDate)
          : null,
        preFunding: p?.preFunding ?? false,
        deferment: p?.deferment ?? "",
        fiftyPercentPrefundInvoiceDate: !isEmpty(
          p?.fiftyPercentPrefundInvoiceDate,
        )
          ? dayjs(p?.fiftyPercentPrefundInvoiceDate)
          : null,
        twentyFivePercentPrefundInvoiceDate: !isEmpty(
          p?.twentyFivePercentPrefundInvoiceDate,
        )
          ? dayjs(p?.twentyFivePercentPrefundInvoiceDate)
          : null,
      }),
      [],
    );

    const onFinishHandler = useCallback(
      async (p: IProject) => {
        modal.confirm({
          title: "Confirm Project Updates",
          icon: <ExclamationCircleOutlined />,
          content: "Are you sure you want to save these project updates?",
          okText: "Save",
          okButtonProps: {
            "data-testid": "projectListProjectFormConfirmButton",
          },
          cancelText: "Cancel",
          onOk: async () => {
            const projectToUpdate = {
              ...omit(
                convertDayjsToDate(
                  updateProjectWithDefaultValues({ ...project, ...p }),
                ),
                [
                  "projectCode",
                  "metadata",
                  "gsi",
                  "searchName",
                  "projectCodeNumber",
                ],
              ),
              id: project.id,
              latestProjectNote: p.latestProjectNote,
            };
            await updateProject(projectToUpdate);
          },
        });
      },
      [modal, project, updateProject, updateProjectWithDefaultValues],
    );

    // Show success notification when we get new data from a successful update
    useEffect(() => {
      if (data && data !== lastSuccessDataRef.current) {
        lastSuccessDataRef.current = data;
        notification.success({
          duration: 3,
          message: "Update project complete",
          description: "Your project has been updated successfully.",
        });
        if (warnings?.length) {
          warnings.forEach((warning) => {
            notification.warning({
              duration: 8,
              message: "Warning",
              description: warning,
            });
          });
        }
        scrollToTop();
        if (onFinish) onFinish();
      }
    }, [data, warnings, scrollToTop, onFinish]);

    // Show error notification
    useEffect(() => {
      if (error) {
        notification.error({
          message: "Update project error",
          description: error?.message,
        });
        executeScroll();
      }
    }, [error, executeScroll]);

    // `data` is the response from the last successful save and outlives a
    // change of `project` — it must only ever seed the form for the project it
    // actually belongs to. Without the id check, saving project A and then
    // navigating to project B rendered A's values under B's name, and saving
    // from there PUT A's field values under B's id.
    const savedThisProject =
      data?.id && project?.id && data.id === project.id ? data : undefined;

    useEffect(() => {
      if (!savedThisProject) return;

      form.setFieldsValue(updateProjectWithDefaultValues(savedThisProject));
    }, [form, updateProjectWithDefaultValues, savedThisProject]);

    // Clear validation errors when switching from edit mode to view mode.
    // Prefer this project's own save response over the (now older) prop.
    useEffect(() => {
      if (!isEditing) {
        form.resetFields();
        const projectData = savedThisProject ?? project;
        form.setFieldsValue(updateProjectWithDefaultValues(projectData));
      }
    }, [isEditing, form, updateProjectWithDefaultValues, project, savedThisProject]);

    const selectPlaceholder = isEditing ? "Select..." : "None";

    return (
      <div data-testid='projectListForm'>
        {contextHolder}
        {/* use a class modifier to render cleaner view-only state */}
        <Form
          form={form}
          name={`projectForm-${project?.id}`}
          data-testid={`projectForm-${project?.id}`}
          className={`update-project-form${
            !isEditing ? " update-project-form--view-only" : ""
          }`}
          layout='vertical'
          requiredMark={false}
          initialValues={updateProjectWithDefaultValues(project)}
          onFinish={onFinishHandler}
          scrollToFirstError
          disabled={!isEditing || user?.role === UserRoles.Subcontractor}
        >
          <Flex vertical gap='2rem 0'>
            {error && (
              <Alert
                ref={errorRef}
                message='Error'
                description={error?.message}
                type='error'
                showIcon
              />
            )}
            <ProjectContactInformation
              form={form}
              currentStage={project?.stage ?? ""}
              isEditing={isEditing}
            />
            <ProjectInformation
              form={form}
              currentStage={project?.stage ?? ""}
              isEditing={isEditing}
            />
            <ProjectInstallationInformation
              form={form}
              currentStage={project?.stage ?? ""}
              isEditing={isEditing}
            />
            <ProjectAccountingInformation
              form={form}
              currentStage={project?.stage ?? ""}
              isEditing={isEditing}
            />
            <ProjectSalesInformation
              form={form}
              currentStage={project?.stage ?? ""}
              isEditing={isEditing}
            />
          </Flex>
          {isEditing && (
            <div className='project-list-update-form-container'>
              <Form.Item
                name='latestProjectNote'
                data-testid='latestProjectNote'
                label='Note'
                rules={[
                  {
                    required: true,
                    message: "Please enter a note.",
                  },
                ]}
              >
                <MentionsComponent
                  options={userMentionOptions ?? []}
                  onChange={(content, mentionIds) => {
                    if (isEmpty(content)) {
                      form.setFieldValue("latestProjectNote", undefined);
                      return;
                    }

                    form.setFieldValue("latestProjectNote", {
                      content,
                      mentionIds,
                    });
                  }}
                />
              </Form.Item>
              <Row gutter={16}>
                <Col>
                  <Form.Item
                    name='stage'
                    data-testid='stage'
                    label='Stage'
                  >
                    <Select
                      style={{ width: 240 }}
                      popupMatchSelectWidth={false}
                      disabled={user?.role === UserRoles.Subcontractor}
                      options={[
                        { value: "", label: selectPlaceholder },
                        ...sortBy(ProjectStages, (s) => s.label),
                      ]}
                      onSelect={(value) => {
                        form.setFieldValue("stage", value);
                      }}
                      placeholder={selectPlaceholder}
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item
                    name='status'
                    data-testid='status'
                    label='Status'
                  >
                    <Select
                      style={{ width: 240 }}
                      popupMatchSelectWidth={false}
                      disabled={user?.role === UserRoles.Subcontractor}
                      options={[
                        { value: "", label: selectPlaceholder },
                        ...projectStatusOptions,
                      ]}
                      onSelect={(value) => {
                        form.setFieldValue("status", value);
                      }}
                      placeholder={selectPlaceholder}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Divider />
              <Flex gap={16} align='center' justify='flex-end'>
                <Button
                  onClick={() => {
                    const hasChanges = form.isFieldsTouched();
                    if (hasChanges) {
                      modal.confirm({
                        title: "Discard Changes",
                        icon: <ExclamationCircleOutlined />,
                        content:
                          "You have unsaved changes. Are you sure you want to discard them?",
                        okText: "Discard",
                        okButtonProps: { danger: true },
                        cancelText: "Keep Editing",
                        onOk: () => {
                          form.resetFields();
                          form.setFieldsValue(
                            updateProjectWithDefaultValues(project)
                          );
                          onCancel?.();
                        },
                      });
                    } else {
                      form.resetFields();
                      form.setFieldsValue(updateProjectWithDefaultValues(project));
                      onCancel?.();
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type='primary'
                  htmlType='submit'
                  data-testid='projectListProjectFormSubmitButton'
                >
                  Save
                </Button>
              </Flex>
            </div>
          )}
        </Form>
      </div>
    );
  },
);
